import { Component, computed, effect, OnDestroy, OnInit, signal } from '@angular/core';

import { ProgressBarModule } from 'primeng/progressbar';
import { Button } from 'primeng/button';
import { AsyncExportStateService } from '../../../pages/measure/services/asyncExportState.service';

@Component({
  selector: 'app-export-progress-dialog',
  standalone: true,
  imports: [ProgressBarModule, Button],
  templateUrl: './export-progress-dialog.component.html',
  styleUrl: './export-progress-dialog.component.scss',
})
export class ExportProgressDialogComponent implements OnInit, OnDestroy {
  visible = signal(false);
  progress = signal(0);
  status = signal('');
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;
  private exportState = computed(() => this.asyncExportStateService.state());

  constructor(private asyncExportStateService: AsyncExportStateService) {
    effect(
      () => {
        const state = this.exportState();
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout);
          this.closeTimeout = null;
        }

        if (state.loading) {
          this.visible.set(true);
          const progressData = state.data;
          if (progressData) {
            const progressValue =
              progressData?.progress ??
              progressData?.data?.progress ??
              progressData?.data?.[0]?.progress ??
              (typeof progressData === 'object' && 'progress' in progressData ? (progressData as any).progress : 0);

            const statusValue =
              progressData?.status ??
              progressData?.data?.status ??
              progressData?.data?.[0]?.status ??
              (typeof progressData === 'object' && 'status' in progressData ? (progressData as any).status : '');

            this.progress.set(progressValue);
            this.status.set(statusValue);
          } else {
            this.progress.set(0);
            this.status.set('');
          }
        } else if (!state.loading && this.visible()) {
          this.closeTimeout = setTimeout(() => {
            this.visible.set(false);
            this.progress.set(0);
            this.status.set('');
          }, 500);
        }

        if (state.error) {
          this.visible.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    const currentState = this.asyncExportStateService.state();
    if (currentState.loading) {
      this.visible.set(true);
      const progressData = currentState.data;
      if (progressData) {
        this.progress.set(
          progressData?.progress ?? progressData?.data?.progress ?? progressData?.data?.[0]?.progress ?? 0,
        );
        this.status.set(progressData?.status ?? progressData?.data?.status ?? progressData?.data?.[0]?.status ?? '');
      }
    }
  }

  ngOnDestroy(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
  }

  close(): void {
    this.visible.set(false);
  }

  getStatusText(): string {
    const currentStatus = this.status();
    if (currentStatus === 'in queue') {
      return 'Export queued...';
    } else if (currentStatus === 'in progress') {
      return 'Exporting survey data...';
    } else if (currentStatus === 'done') {
      return 'Export completed!';
    } else if (currentStatus === 'failed') {
      return 'Export failed';
    }
    return 'Processing...';
  }
}
